import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useConnection } from "../context/ConnectionContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  History,
  Database,
  Server,
  Loader2,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Copy,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const QueryHistoryPage = () => {
  const { getAuthHeader } = useAuth();
  const { selectedConnection } = useConnection();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [selectedConn, setSelectedConn] = useState("");

  useEffect(() => {
    if (selectedConnection && selectedConnection.id) {
      setSelectedConn(selectedConnection.id);
    }
  }, [selectedConnection]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [selectedConn]);

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API_URL}/connections`, {
        headers: getAuthHeader()
      });
      setConnections(response.data);
    } catch (error) {
      toast.error("Failed to fetch connections");
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const url = selectedConn 
        ? `${API_URL}/query/history?connection_id=${selectedConn}`
        : `${API_URL}/query/history`;
      const response = await axios.get(url, {
        headers: getAuthHeader()
      });
      setHistory(response.data);
    } catch (error) {
      toast.error("Failed to fetch history");
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      const url = selectedConn 
        ? `${API_URL}/query/history?connection_id=${selectedConn}`
        : `${API_URL}/query/history`;
      await axios.delete(url, {
        headers: getAuthHeader()
      });
      toast.success("History cleared");
      fetchHistory();
    } catch (error) {
      toast.error("Failed to clear history");
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await axios.delete(`${API_URL}/query/history/${id}`, {
        headers: getAuthHeader()
      });
      toast.success("History item deleted");
      fetchHistory();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleRunQuery = (query, connId) => {
    // Navigate to query editor with the query pre-filled
    // We'll store it in sessionStorage for simplicity
    sessionStorage.setItem('pendingQuery', JSON.stringify({ query, connId }));
    navigate('/dashboard/query');
  };

  const handleCopyQuery = (query) => {
    navigator.clipboard.writeText(query);
    toast.success("Query copied to clipboard");
  };

  const getConnectionName = (connId) => {
    const conn = connections.find(c => c.id === connId);
    return conn?.name || 'Unknown';
  };

  const getConnectionType = (connId) => {
    const conn = connections.find(c => c.id === connId);
    return conn?.db_type || 'unknown';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-6">
        <div>
          <h1 className="text-xl font-bold">Query History</h1>
          <p className="text-sm text-muted-foreground">View your recent query executions</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedConn || "all"} onValueChange={(value) => setSelectedConn(value === "all" ? "" : value)}>
            <SelectTrigger className="w-48" data-testid="history-connection-filter">
              <SelectValue placeholder="All connections" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All connections</SelectItem>
              {connections.map(conn => (
                <SelectItem key={conn.id} value={conn.id}>
                  <div className="flex items-center gap-2">
                    {conn.db_type === 'mongodb' ? (
                      <Database className="w-4 h-4 text-green-500" />
                    ) : (
                      <Server className="w-4 h-4 text-blue-500" />
                    )}
                    {conn.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="gap-1 text-destructive hover:text-destructive" disabled={history.length === 0} data-testid="clear-history-btn">
                <Trash2 className="w-4 h-4" />
                Clear History
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear Query History</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to clear {selectedConn ? 'this connection\'s' : 'all'} query history? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearHistory} className="bg-destructive hover:bg-destructive/90">
                  Clear
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </header>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <History className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No query history</h3>
              <p className="text-muted-foreground">
                Your executed queries will appear here
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Status</TableHead>
                  <TableHead className="w-40">Connection</TableHead>
                  <TableHead>Query</TableHead>
                  <TableHead className="w-24">Rows</TableHead>
                  <TableHead className="w-24">Time</TableHead>
                  <TableHead className="w-44">Executed</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(item => (
                  <TableRow key={item.id} data-testid={`history-item-${item.id}`}>
                    <TableCell>
                      {item.success ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-destructive" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getConnectionType(item.connection_id) === 'mongodb' ? (
                          <Database className="w-4 h-4 text-green-500" />
                        ) : (
                          <Server className="w-4 h-4 text-blue-500" />
                        )}
                        <span className="truncate">{getConnectionName(item.connection_id)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs font-mono bg-muted/30 px-2 py-1 rounded block truncate max-w-md">
                        {item.query}
                      </code>
                      {item.error && (
                        <p className="text-xs text-destructive mt-1 truncate max-w-md">
                          {item.error}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {item.row_count}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {item.execution_time_ms.toFixed(0)}ms
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyQuery(item.query)}
                          data-testid={`copy-query-btn-${item.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRunQuery(item.query, item.connection_id)}
                          data-testid={`run-query-btn-${item.id}`}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-history-btn-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default QueryHistoryPage;
